<template>
  <form @submit.prevent="handleSubmit">
    <!-- Cardholder Name -->
    <div class="mb-4">
      <label for="cardholderName" class="block text-sm font-medium text-gray-700 mb-2">
        Cardholder Name
      </label>
      <input
        id="cardholderName"
        v-model="cardholderName"
        type="text"
        required
        class="w-full px-3 py-2 text-black border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
        placeholder="John Doe"
      />
    </div>

    <!-- Payment Element -->
    <div class="mb-4">
      <label class="block text-sm font-medium text-gray-700 mb-2">
        Card Information
      </label>
      <div class="w-full border border-gray-300 rounded-md p-3 focus-within:ring-2 focus-within:ring-yellow-500 focus-within:border-transparent">
        <PaymentElement @loaderstart="onLoaderStart" @change="onPaymentChange" />
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
      <p class="text-red-600 text-sm">{{ errorMessage }}</p>
    </div>

    <!-- Submit Button -->
    <button
      type="submit"
      :disabled="!stripe || !elements || isProcessing || !isFormValid"
      class="w-full bg-yellow-500 text-black py-3 px-4 rounded-md font-semibold hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <span v-if="!isProcessing">Complete Rental - ${{ rentalPrice }}</span>
      <span v-else class="flex items-center justify-center">
        <font-awesome-icon icon="spinner" class="animate-spin mr-2" />
        Processing...
      </span>
    </button>
  </form>
</template>

<script setup>
import { useAuth } from '@/composables/useAuth'
import { createPaymentIntent } from '@/utils/paymentIntent'
import { ref, computed } from 'vue'
import { PaymentElement, useElements, useStripe } from 'vue-stripe'

// Props
const props = defineProps({
  rentalPrice: {
    type: [String, Number],
    required: true
  },
  videoId: {
    type: String,
    required: true
  }
})

const defaultFeedId = import.meta.env.VITE_DEFAULT_FEED_ID


// Emits
const emit = defineEmits(['payment-complete', 'payment-error'])

// Vue Stripe composables
const stripe = useStripe()
const elements = useElements()

// Form data
const cardholderName = ref('')
const errorMessage = ref('')
const isProcessing = ref(false)
const { user } = useAuth()

// Computed
const isFormValid = computed(() => {
  return cardholderName.value.trim()
})

// Methods
const onLoaderStart = () => {
  console.log('Payment element loader started')
}

const onPaymentChange = (event) => {
  if (event.error) {
    errorMessage.value = event.error.message
  } else {
    errorMessage.value = ''
  }
}

const handleSubmit = async () => {
  if (!stripe.value || !elements.value) {
    errorMessage.value = 'Payment system not ready. Please try again.'
    return
  }

  isProcessing.value = true
  errorMessage.value = ''

  try {
    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.value.submit()
    if (submitError) {
      throw new Error(submitError.message)
    }

    const { client_secret: clientSecret } = await createIntent()

    if (!clientSecret) {
      throw new Error('Failed to process payment. Please try again later.')
    }

    const { error, paymentIntent } = await stripe.value.confirmPayment({
      elements: elements.value,
      clientSecret,
      confirmParams: {
        return_url: window.location.origin,
        payment_method_data: {
          billing_details: {
            name: cardholderName.value,
          }
        }
      },
      redirect: 'if_required'
    })

    if (error) {
      throw new Error(error.message)
    } 

    if (paymentIntent.status !== 'succeeded') {
      throw new Error(paymentIntent.last_payment_error?.message)
    }

    // Payment complete
    emit('payment-complete', {
      videoId: props.videoId,
      amount: props.rentalPrice,
      customerName: cardholderName.value,
      paymentIntent
    })
    
  } catch (error) {
    console.error('Payment error:', error)
    errorMessage.value = error.message || 'Payment failed'
    emit('payment-error', error)
  } finally {
    isProcessing.value = false
  }
}

/**
 * Create a PaymentIntent
 * @returns {Promise<string>} The client secret
 */
const createIntent = async () => {
  try {
    const response = await createPaymentIntent(user.value.email, props.videoId, defaultFeedId)
    return response
  } catch (error) {
    console.error('Error creating payment intent:', error)
    return ""
  }
}

// Expose form data for parent component
defineExpose({
  cardholderName,
  isProcessing
})
</script>
